/**
 * hourlySummaryApi.js — API functions for the Hourly Summary Report page.
 *
 * fetchHourlySummaryReport — runs the hourly summary report query
 *
 * All other shared functions (filters, notes, reasons) are re-exported
 * from alertDetailsApi.js to avoid code duplication.
 */

// Re-export shared functions from alertDetailsApi
import { getAuthHeaders } from '../../../../services/auth';

export {
    fetchFilterOptions,
    fetchPreformNotes,
    addEventNote,
    editEventReason,
} from '../../alert-details/api/alertDetailsApi.js';

// ---------------------------------------------------------------------------
// Internals (same base URL / header pattern)
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Hourly Summary Report
// ---------------------------------------------------------------------------

/**
 * Runs the hourlySummary report query with all filter fields.
 *
 * Uses a direct POST to the report endpoint with rep_type: "hourlysummary".
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {object} opts.filters     — all report-level filter selections
 * @returns {Promise<object>} — { records, summary, max, min, avg, savg, mostActive, overlimit, url, ErrorCode }
 */
export async function fetchHourlySummaryReport({ campusId, username, startDate, endDate, filters = {} }) {
    const body = {
        rep_type: 'hourlysummary',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        device_category_filter: filters.deviceCategories || '',
        device_filter: filters.devices || '',
        area_type_filter: filters.areaTypes || '',
        reason_filter: filters.reasons || '',
        user_filter: filters.users || '',
        event_type_filter: filters.eventTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: filters.residentGroups || '',
        area_filter: filters.areas || '',
        area_group_filter: filters.areaGroups || '',
        user_group_filter: filters.userGroups || '',
        resp_filter_items: filters.responseTime || [false, 'lt', 60],
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();

        if (raw?.ErrorCode) {
            return { ErrorCode: raw.ErrorCode };
        }

        // Report endpoint wraps data in { Status, Res: {...} }
        const data = raw?.Res || raw;

        if (data?.ErrorCode) {
            return { ErrorCode: data.ErrorCode };
        }

        return {
            records: data?.records || [],
            summary: data?.summary || [],
            max: data?.max ?? null,
            min: data?.min ?? null,
            avg: data?.avg ?? null,
            savg: data?.savg ?? null,
            mostActive: data?.mostActive || [],
            overlimit: Boolean(data?.overlimit),
            url: data?.url || '',
        };
    } catch (err) {
        console.error('[hourlySummaryApi] fetchHourlySummaryReport error:', err);
        return { ErrorCode: 10 };
    }
}
